import { useFormat } from '@shared/hooks/useFormat';
import { GlobalMessages } from '@shared/localization/messages.ts';
import dayjs from 'dayjs';
import uniq from 'lodash/uniq';
import { useCallback, useMemo, useState } from 'react';

import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import { useUserQueryActions } from '@/api/hooks/userQueryActions';
import type {
  BasicEstimationsPerProjectsResponsePaginatedResponse,
  UserResponse,
} from '@/api/schema/data-contracts';
import { DataTableComponent } from '@/components/organism/DataTable';
import { eExportFormat } from '@/components/organism/DataTable/model';
import { useEstimationStageOptions } from '@/components/organism/Estimates/hooks/useEstimationStageOptions';
import { estimationUnitMessageDefinitions } from '@/context/localization/enums/messages';
import { useMessages } from '@/hooks/messages';
import type { EstimationVersion } from '@/types';

import { EstimationVersionsMessages } from './messages';
import { columnsConfiguration } from './model';

interface EstimationVersionsContainerProps {
  projectId: string;
}

const EstimationVersionsContainer = ({ projectId }: EstimationVersionsContainerProps) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(EstimationVersionsMessages);
  const estimationUnitMessages = useMessages(estimationUnitMessageDefinitions);
  const estimationStageOptions = useEstimationStageOptions();
  const { formatNumber } = useFormat(globalMessages.yes(), globalMessages.no());

  const [pageNumber, setPageNumber] = useState(1);

  const { getUser } = useUserQueryActions();

  const { useGetEstimations } = useEstimationQueryActions();
  const { data: estimations } = useGetEstimations({
    projectId,
    pageNumber,
    onlyCurrent: false,
  }) as { data: BasicEstimationsPerProjectsResponsePaginatedResponse | undefined };

  const versionsData = useMemo<EstimationVersion[]>(() => {
    if (estimations) {
      return estimations.items.map((item, index) => {
        const {
          estimation: {
            id,
            versionName,
            stage,
            createdAt,
            createdById,
            updatedAt,
            updatedById,

            calculatedResult,
            unit,
          },
        } = item;
        return {
          index: (index + 1).toString(),
          estimationId: id,
          versionName,
          status: stage,
          lastUpdatedAt: dayjs(updatedAt),
          lastUpdatedBy: updatedById ?? '',
          createdAt: dayjs(createdAt),
          createdBy: createdById ?? '',
          estimate: `${formatNumber(calculatedResult)} ${estimationUnitMessages[unit]()}`,
        };
      });
    }
    return [];
  }, [estimationUnitMessages, estimations, formatNumber]);

  const getExportData = useCallback(async () => {
    const userIds = uniq(versionsData.flatMap((row) => [row.createdBy, row.lastUpdatedBy]));

    const userByUserId = (
      await Promise.all(
        userIds.map(async (userId) => {
          const data = await getUser(userId);
          return { userId, data };
        }),
      )
    ).reduce<Record<string, UserResponse>>((acc, { userId, data }) => {
      acc[userId] = data;
      return acc;
    }, {});

    return versionsData.map((row) => {
      const createdByUser = userByUserId[row.createdBy];
      const updatedByUser = userByUserId[row.lastUpdatedBy];
      return {
        ...row,
        createdBy: `${createdByUser.forename} ${createdByUser.surname}`,
        lastUpdatedBy: `${updatedByUser.forename} ${updatedByUser.surname}`,
        status: estimationStageOptions.find((x) => x.value === row.status)?.label,
      };
    });
  }, [estimationStageOptions, getUser, versionsData]);

  return (
    <div>
      {estimations && (
        <DataTableComponent<EstimationVersion>
          columnsConfiguration={columnsConfiguration(messages)}
          pagination={{ totalItems: estimations.totalCount, recordsPerPage: 20 }}
          data={versionsData}
          getExportData={getExportData}
          showColumnSelector
          exportConfiguration={[eExportFormat.CSV, eExportFormat.PRINT, eExportFormat.PDF]}
          onPageChange={setPageNumber}
        />
      )}
    </div>
  );
};

export default EstimationVersionsContainer;
